import type { ValidationConfig, ValidationEngineRule, ValidationNode } from './types';
import { isCollectionNode, isFieldNode, isGroupNode } from './guards';

const DEFAULT_FLOW = 'default';

const pickSeparator = (flow: string): string => {
    const hasPlus = flow.includes('+');
    const hasPipe = flow.includes('|');
    if (hasPlus && !hasPipe) return '+';
    if (hasPipe && !hasPlus) return '|';
    return '|';
};

const stripFlowFromRuleFlow = (flow: string, flowToRemove: string): string | null => {
    const tokens = flow
        .split(/[+|]/g)
        .map((t) => t.trim())
        .filter(Boolean);

    if (!tokens.includes(flowToRemove)) return flow;

    const remaining = Array.from(new Set(tokens.filter((t) => t !== flowToRemove)));
    if (remaining.length === 0) return null;

    if (remaining.length === 1 && remaining[0] === DEFAULT_FLOW) return '';

    return remaining.join(pickSeparator(flow));
};

const stripFlowFromRule = (rule: ValidationEngineRule, flowToRemove: string): ValidationEngineRule | null => {
    if (!rule.flow) {
        return flowToRemove === DEFAULT_FLOW ? null : rule;
    }

    const stripped = stripFlowFromRuleFlow(rule.flow, flowToRemove);
    if (stripped === null) return null;

    if (stripped === '') {
        const { flow: _flow, ...rest } = rule;
        return rest;
    }

    if (stripped === rule.flow) return rule;

    return {
        ...rule,
        flow: stripped
    };
};

const stripFlowFromConfigNode = (node: ValidationNode, flowToRemove: string): ValidationNode => {
    if (isFieldNode(node)) {
        let changed = false;
        const nextRules: ValidationEngineRule[] = [];

        for (const rule of node.rules) {
            const next = stripFlowFromRule(rule, flowToRemove);
            if (next === null) {
                changed = true;
                continue;
            }
            if (next !== rule) changed = true;
            nextRules.push(next);
        }

        if (!changed) return node;
        return {
            ...(node as any),
            rules: nextRules
        };
    }

    if (isGroupNode(node)) {
        const nextGroup = stripFlowFromValidationConfig(node.$group, flowToRemove);
        if (nextGroup === node.$group) return node;

        return {
            ...(node as any),
            $group: nextGroup
        };
    }

    if (isCollectionNode(node)) {
        const nextCollection = stripFlowFromValidationConfig(node.$collection, flowToRemove);
        if (nextCollection === node.$collection) return node;

        return {
            ...(node as any),
            $collection: nextCollection
        };
    }

    return stripFlowFromValidationConfig(node as ValidationConfig, flowToRemove);
};

const stripFlowFromValidationConfig = (config: ValidationConfig, flowToRemove: string): ValidationConfig => {
    let changed = false;
    const nextConfig: ValidationConfig = {};

    for (const [key, node] of Object.entries(config)) {
        const nextNode = stripFlowFromConfigNode(node as ValidationNode, flowToRemove);
        if (nextNode !== node) changed = true;
        (nextConfig as any)[key] = nextNode;
    }

    return changed ? nextConfig : config;
};

/**
 * Returns a new `ValidationConfig` with all rules for `flowToRemove` removed.
 *
 * - Rules tagged `flow: flowToRemove` are dropped.
 * - Rules tagged with multi-flow expressions (e.g. `default|contract`, `default+contract`) are updated to remove `flowToRemove`.
 * - If the remaining flow set is only `default`, the `flow` property is omitted.
 */
export const removeFlowRules = (config: ValidationConfig, flowToRemove: string): ValidationConfig =>
    stripFlowFromValidationConfig(config, flowToRemove);

export const removeContractFlowRules = (config: ValidationConfig): ValidationConfig => removeFlowRules(config, 'contract');
